import React, { ReactNode, useContext, useMemo, useState } from "react";
import styled from "styled-components";
import SortSelect from "common/Select/SortSelect";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces, SortingOrder } from "@shared/types";

export interface RowSortOption<T> {
  key: string;
  label: string;
  compare: (a: T, b: T) => number;
}

interface RowListProps<T> {
  title: ReactNode;
  items?: T[];
  getKey: (item: T) => React.Key;
  renderRow: (item: T) => ReactNode;
  /** Keeps the items that match the header search. */
  filter?: (item: T, search: string) => boolean;
  sortOptions: RowSortOption<T>[];
  loading?: boolean;
  empty: ReactNode;
  /** Replaces the empty state when the search matches nothing. */
  searchEmpty?: ReactNode;
  "data-testid"?: string;
}

/**
 * The phone version of a table: a flush panel with the title, count and
 * sort in its header and one row per item (16px sides, hairlines), shown
 * 20 at a time.
 */
const RowList = <T,>({
  title,
  items,
  getKey,
  renderRow,
  filter,
  sortOptions,
  loading,
  empty,
  searchEmpty,
  "data-testid": testId,
}: RowListProps<T>) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { search } = useContext(PageWrapperContext);
  const searchValue = search?.value || "";
  const [sortBy, setSortBy] = useState(sortOptions[0]?.key);
  const [sortDirection, setSortDirection] = useState(SortingOrder.DESCEND);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const rows = useMemo(() => {
    const option = sortOptions.find((sortOption) => sortOption.key === sortBy);
    const filtered = (items || []).filter(
      (item) => !filter || !searchValue || filter(item, searchValue)
    );
    if (!option) return filtered;
    return filtered
      .slice()
      .sort((a, b) => option.compare(a, b) * sortDirection);
  }, [items, filter, searchValue, sortBy, sortDirection, sortOptions]);

  const getContent = () => {
    if (loading && !items?.length)
      return Array.from({ length: 4 }, (_, index) => (
        <Row key={index} aria-hidden={true}>
          <Line>
            <Skeleton width={120} height={14} />
            <Skeleton width={64} height={14} />
          </Line>
          <Line>
            <Skeleton width={90} height={10} />
            <Skeleton width={48} height={10} />
          </Line>
        </Row>
      ));
    if (!rows.length)
      return searchValue && items?.length ? searchEmpty ?? empty : empty;
    return (
      <>
        {rows.slice(0, visibleCount).map((item) => (
          <Row key={getKey(item)}>{renderRow(item)}</Row>
        ))}
        {rows.length > visibleCount && (
          <ShowMore
            type="button"
            onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
          >
            {t("filters.common.show_more")}
          </ShowMore>
        )}
      </>
    );
  };

  return (
    <Panel
      title={title}
      count={items?.length || undefined}
      actions={
        rows.length > 1 && (
          <SortSelect<string>
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            sortValues={sortOptions.map((option) => option.key)}
            labelMapper={(key) =>
              sortOptions.find((option) => option.key === key)?.label ?? key
            }
          />
        )
      }
      flush={true}
      data-testid={testId}
    >
      {getContent()}
    </Panel>
  );
};

export default RowList;

const PAGE_SIZE = 20;

/* ---- Row building blocks (shared by every row list) ---- */

export const Line = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
`;
export const RowTitle = styled.div`
  min-width: 0;
  ${newLookType.body};
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const RowMeta = styled.div`
  min-width: 0;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (max-width: 359.98px) {
    white-space: normal;
    overflow-wrap: anywhere;
  }
`;
export const RowFigure = styled.div`
  flex: none;
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  text-align: right;
`;
export const RowSubFigure = styled(RowMeta)`
  flex: none;
  text-align: right;
`;
const Row = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-height: 60px;
  /* Lines up with the panel header: title at 20px, controls at 16px. */
  padding: 10px 16px 10px 20px;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const ShowMore = styled.button`
  width: 100%;
  height: 44px;
  border: 0;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  border-radius: 0 0 12px 12px;
  background: transparent;
  font: inherit;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).accent};
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: -2px;
  }
`;
